/**
 * Telegram Mini App bridge (§23).
 *
 * Everything is optional-chained and feature-detected: the same build has to run
 * inside Telegram *and* in a plain browser during development, and an older
 * Telegram client may not have the newer methods.
 */

type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
type NotificationType = 'error' | 'success' | 'warning';

interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: {
    start_param?: string;
    user?: { id: number; username?: string; language_code?: string };
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  viewportHeight: number;
  viewportStableHeight: number;
  isExpanded: boolean;
  /** Bot API 8.0+. */
  isFullscreen?: boolean;
  ready(): void;
  expand(): void;
  /** Bot API 8.0+. */
  requestFullscreen?(): void;
  /** Bot API 7.7+: stops a downward scroll at the top from closing the app. */
  disableVerticalSwipes?(): void;
  close(): void;
  isVersionAtLeast(version: string): boolean;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  enableClosingConfirmation?(): void;
  disableClosingConfirmation?(): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  openTelegramLink(url: string): void;
  onEvent(event: string, handler: () => void): void;
  offEvent(event: string, handler: () => void): void;
  HapticFeedback?: {
    impactOccurred(style: HapticStyle): void;
    notificationOccurred(type: NotificationType): void;
    selectionChanged(): void;
  };
  BackButton?: {
    isVisible: boolean;
    show(): void;
    hide(): void;
    onClick(handler: () => void): void;
    offClick(handler: () => void): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

function webApp(): TelegramWebApp | null {
  const app = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
  // Outside Telegram the object is absent; in a browser tab opened directly it
  // exists but carries no initData, which is equally unusable for auth.
  return app ?? null;
}

export function isTelegramClient(): boolean {
  const app = webApp();
  return Boolean(app && app.initData && app.initData.length > 0);
}

export function getInitData(): string | null {
  const app = webApp();
  return app?.initData && app.initData.length > 0 ? app.initData : null;
}

export function getStartParam(): string | null {
  const app = webApp();
  const fromTelegram = app?.initDataUnsafe?.start_param;
  if (fromTelegram) return fromTelegram;
  // Supports opening the app with ?startapp=CODE in a browser during development.
  if (typeof window !== 'undefined') {
    const param = new URLSearchParams(window.location.search).get('startapp');
    if (param) return param;
  }
  return null;
}

/** Phones, where fullscreen means the whole screen rather than a desktop window. */
const MOBILE_PLATFORMS = new Set(['android', 'android_x', 'ios']);

/**
 * Called once at startup: tell Telegram we are painted, and take the whole
 * screen.
 *
 * On phones with Bot API 8.0+ the app asks for fullscreen: Telegram's header
 * turns transparent and its close and menu buttons float over the app, which
 * is why every screen pads its top by --safe-top (tokens.css) — Telegram keeps
 * the inset variables that feeds on up to date itself. Older clients and the
 * desktop apps keep the plain expanded view. Vertical swipes are switched off
 * so scrolling a long screen back to the top cannot pull the app closed.
 */
export function initTelegram(): void {
  const app = webApp();
  if (!app) return;
  app.ready();
  app.expand();
  app.setHeaderColor?.('#0D1015');
  app.setBackgroundColor?.('#08090C');

  if (app.isVersionAtLeast('7.7')) app.disableVerticalSwipes?.();

  if (
    app.isVersionAtLeast('8.0') &&
    MOBILE_PLATFORMS.has(app.platform) &&
    !app.isFullscreen
  ) {
    try {
      app.requestFullscreen?.();
    } catch {
      // An unsupported client reports it through fullscreenFailed; the expanded
      // view it keeps is still correct.
    }
  }
}

let hapticsEnabled = true;

/**
 * The language Telegram says the client is in, e.g. `ru`, `pt-BR`, `ar`.
 *
 * Used for the very first paint, before the account's own `settings.language`
 * has arrived — so someone opening the app for the first time sees their own
 * language rather than English followed by a flicker. Unverified (it comes from
 * `initDataUnsafe`), which is fine: the worst a forged value can do is pick the
 * wrong language for the person who forged it.
 */
export function getTelegramLanguage(): string | null {
  return webApp()?.initDataUnsafe?.user?.language_code ?? null;
}

/** Follows the user's Preferences setting (profile settings `haptics`). */
export function setHapticsEnabled(enabled: boolean): void {
  hapticsEnabled = enabled;
}

export const haptics = {
  impact(style: HapticStyle = 'light'): void {
    if (hapticsEnabled) webApp()?.HapticFeedback?.impactOccurred(style);
  },
  notify(type: NotificationType): void {
    if (hapticsEnabled) webApp()?.HapticFeedback?.notificationOccurred(type);
  },
  select(): void {
    if (hapticsEnabled) webApp()?.HapticFeedback?.selectionChanged();
  },
};

/** Closes the Mini App inside Telegram; outside it, reloads to the start. */
export function closeApp(): void {
  const app = webApp();
  if (app) app.close();
  else window.location.assign('/');
}

export const backButton = {
  show(handler: () => void): () => void {
    const button = webApp()?.BackButton;
    if (!button) return () => {};
    button.onClick(handler);
    button.show();
    return () => {
      button.offClick(handler);
      button.hide();
    };
  },
  hide(): void {
    webApp()?.BackButton?.hide();
  },
};

/**
 * Asks Telegram to confirm before closing. Used only while something is
 * genuinely at stake — an ad in flight or a withdrawal being submitted.
 */
export function setClosingConfirmation(enabled: boolean): void {
  const app = webApp();
  if (!app) return;
  if (enabled) app.enableClosingConfirmation?.();
  else app.disableClosingConfirmation?.();
}

/** Opens a link the way Telegram expects, falling back to a normal tab. */
export function openExternal(url: string): void {
  const app = webApp();
  if (!app) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (/^https?:\/\/t\.me\//i.test(url)) app.openTelegramLink(url);
  else app.openLink(url);
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Falls through to the legacy path below.
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

/** Telegram's viewport height, kept in sync so `100dvh` is never wrong. */
export function observeViewport(onChange: (height: number) => void): () => void {
  const app = webApp();
  if (!app) return () => {};
  const handler = () => onChange(app.viewportStableHeight || app.viewportHeight);
  // Entering or leaving fullscreen changes the height too.
  const events = ['viewportChanged', 'fullscreenChanged'];
  events.forEach((event) => app.onEvent(event, handler));
  handler();
  return () => events.forEach((event) => app.offEvent(event, handler));
}
